import type { DashEnergyWidgetConfig, DashEnergyWidgetTypes } from './dash-energy-types';
import { buildEnergyRangeControls } from 'src/components/EnergyMonitoring/helpers/period_helpers';
import { ENERGY_MONITORING_PERIODS } from 'src/components/EnergyMonitoring/helpers/period_helpers';
import { buildHeatmapYearControls } from 'src/components/EnergyMonitoring/helpers/heatmap_period_helpers';
import { getEnergyConfigForType } from './dash-energy-helpers';

export const getControlsForEnergyConfig = (config: DashEnergyWidgetConfig) =>
	buildEnergyRangeControls(config.period);

export const getControlsForHeatmapConfig = () => buildHeatmapYearControls();

type EnergyPeriodFormatter = (key: string, options?: { default?: string }) => string;

const LIVE_ONLY_WIDGET_TYPES = new Set<DashEnergyWidgetTypes>([
	'dashEnergyKpiLive',
	'dashEnergyTopConsumers',
	'dashEnergyConsumerMix',
	'dashEnergyFlow',
	'dashEnergyPeak',
	'dashEnergyInsights',
	'dashEnergyPvDistribution',
	'dashEnergyPhase',
	'dashEnergyEnvironmental',
]);

const LIVE_INFO_CATEGORIES = new Set([
	'load',
	'solar',
	'selfPowered',
	'gridImport',
	'currentTariff',
	'topConsumer',
	'standby',
]);

const PERIOD_INFO_CATEGORIES = new Set([
	'periodConsumption',
	'periodGeneration',
	'tariff',
	'offPeak',
	'peakDemand',
]);

/** Label shown beside an Energy widget's section name. */
export const getEnergyWidgetPeriodLabel = (
	type: DashEnergyWidgetTypes,
	config: Partial<DashEnergyWidgetConfig> | undefined,
	format: EnergyPeriodFormatter,
): string => {
	if (type === 'dashEnergyHeatmap') {
		const heatmapConfig = getEnergyConfigForType('dashEnergyHeatmap', config);
		const isMonthly = heatmapConfig.heatmapVariant === 'minimal';
		return format(isMonthly ? 'energy_monitoring_this_month' : 'energy_monitoring_this_year', {
			default: isMonthly ? 'This month' : 'This year',
		});
	}

	const normalized = getEnergyConfigForType(type, config);
	if (LIVE_ONLY_WIDGET_TYPES.has(type)) {
		return format('label_live', { default: 'Live' });
	}

	const option = ENERGY_MONITORING_PERIODS.find((period) => period.id === normalized.period);
	if (type === 'dashEnergyInfo') {
		const categories = (normalized as DashEnergyWidgetConfig<'dashEnergyInfo'>).infoCategories ?? [];
		const hasLive = categories.some((category) => LIVE_INFO_CATEGORIES.has(category));
		const hasPeriod = categories.some((category) => PERIOD_INFO_CATEGORIES.has(category));
		if (hasLive && !hasPeriod) return format('label_live', { default: 'Live' });
		if (hasLive && hasPeriod && option) {
			return `${format('label_live', { default: 'Live' })} + ${format(option.labelKey, {
				default: option.labelDefault,
			})}`;
		}
	}
	return option ? format(option.labelKey, { default: option.labelDefault }) : '';
};
